import React, { createContext, useContext, useState, useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { User as SchemaUser } from "@shared/schema";

export type User = Omit<SchemaUser, "password" | "createdAt"> & {
  createdAt: string;
  isAdmin?: boolean;
};

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: { email: string; password: string; firstName?: string; lastName?: string; fullName?: string }) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { toast } = useToast();
  const [error, setError] = useState<Error | null>(null);

  // Fetch the current user
  const { 
    data: user,
    isLoading,
    refetch
  } = useQuery<User | null, Error>({
    queryKey: ['/api/auth/me'],
    queryFn: async () => {
      try {
        const res = await apiRequest('GET', '/api/auth/me');
        if (!res.ok) {
          if (res.status === 401) {
            return null;
          }
          throw new Error('Failed to fetch user');
        }
        return res.json();
      } catch (error) {
        setError(error as Error);
        return null;
      }
    },
  });

  // Login function
  const login = async (email: string, password: string) => {
    try {
      console.log("Attempting login with:", { email });
      
      // Use email field as configured in passport's LocalStrategy (server expects 'email')
      const res = await apiRequest('POST', '/api/auth/login', { 
        email, // Must match the usernameField in server LocalStrategy
        password 
      });
      
      console.log("Login response status:", res.status);
      
      if (!res.ok) {
        // Extract error details if available
        const errorData = await res.json().catch(() => ({ message: "Authentication failed" }));
        console.error("Login failed with status:", res.status, "Error data:", errorData);
        
        // Check if we have a message about no account found
        if (errorData.message?.includes("No account found")) {
          throw new Error("No account found with this email address. Please check your spelling or sign up.");
        } else {
          throw new Error(errorData.message || "Authentication failed. Please check your credentials.");
        }
      }
      
      // Parse the response body - clone the response first since we can only read it once
      const userData = await res.clone().json();
      console.log("Login successful, user data:", userData);
      
      // Store JWT token in localStorage
      if (userData.token) {
        localStorage.setItem('auth_token', userData.token);
      }
      
      // Remove token from user data before storing in cache
      const { token, ...userDataWithoutToken } = userData;

      // Clear ALL cached data + saved business selection so welcome dashboard appears
      queryClient.clear();
      localStorage.removeItem('activeBusinessId');
      const userId = userDataWithoutToken.id;
      if (userId) localStorage.removeItem(`activeBusinessId_user_${userId}`);

      // Update the query cache with the user data
      queryClient.setQueryData(['/api/auth/me'], userDataWithoutToken);

      // Hard navigate to dashboard to fully reset React component tree
      const admin = userDataWithoutToken.email === "hello@zentrobooks.com";
      window.location.href = admin ? "/admin/dashboard" : "/dashboard";
    } catch (err) {
      console.error("Login error:", err);
      setError(err as Error);
      toast({
        title: 'Login failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  // Logout function — clears all auth + business state
  const logout = async () => {
    try {
      const res = await apiRequest('POST', '/api/auth/logout');
      if (!res.ok) {
        throw new Error('Logout failed');
      }
      // Clear all session/business state to prevent cross-user leakage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('activeBusinessId');
      // Clear per-user business keys
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('activeBusinessId_user_')) {
          localStorage.removeItem(key);
        }
      }
      queryClient.setQueryData(['/api/auth/me'], null);
      queryClient.setQueryData(['/api/businesses'], []);
      // Clear all cached queries to prevent stale data from leaking to next login
      queryClient.clear();
      toast({
        title: 'Logged out',
        description: 'You have been successfully logged out.',
      });
    } catch (err) {
      setError(err as Error);
      toast({
        title: 'Logout failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  // Register function
  const register = async (data: { email: string; password: string; firstName?: string; lastName?: string; fullName?: string }) => {
    try {
      // Process name fields
      let registerData = {
        ...data,
        username: data.email // Set username equal to email - still needed for database schema
      };
      
      // If we have firstName and lastName but no fullName, combine them
      if ((data.firstName || data.lastName) && !data.fullName) {
        registerData.fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
      }
      
      // If we have fullName but no firstName/lastName, try to split it
      if (data.fullName && !data.firstName && !data.lastName) {
        const nameParts = data.fullName.split(' ');
        if (nameParts.length > 1) {
          registerData.firstName = nameParts[0];
          registerData.lastName = nameParts.slice(1).join(' ');
        } else {
          registerData.firstName = data.fullName;
          registerData.lastName = '';
        }
      }
      
      const res = await apiRequest('POST', '/api/auth/signup', registerData);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Registration failed');
      }
      const userData = await res.json();
      queryClient.setQueryData(['/api/auth/me'], userData);
      
      // Use firstName for greeting if available, otherwise fall back to fullName or email
      const displayName = userData.firstName || userData.fullName || data.email;
      toast({
        title: 'Registration successful',
        description: `Welcome to ZentroBooks, ${displayName}!`,
      });
    } catch (err) {
      setError(err as Error);
      toast({
        title: 'Registration failed',
        description: (err as Error).message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ user: user ?? null, isLoading, error, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
